import React, { useState, useMemo } from "react";
import { Box, CircularProgress, Collapse, Typography } from "@mui/material";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import { buildAgentChatUrl } from "../boards/commentUtils";
import AgentAvatar from "../AgentAvatar";
import CommonStatusPill from "../../common/StatusPill";
import { StatusBadge } from "../SessionRow";
import { AVATAR_SIZE, AVATAR_RADIUS, TEXT_INDENT } from "./conversationConstants";
import {
  messageRowSx,
  getAgentAvatarClickHandler,
  getClickableAgentAvatarSx,
} from "./conversationUtils";

function parseJson(val) {
  if (!val) return null;
  if (typeof val === "object") return val;
  try {
    return JSON.parse(val);
  } catch {
    return null;
  }
}

export default function SpawnWorkerCard({
  args,
  result,
  agentName,
  agentId,
  grouped,
  onOpenAgentSettings,
  lifecycleState,
  compact = false,
  liveWorkerStatus,
}) {
  const [expanded, setExpanded] = useState(false);
  const avatarClick = getAgentAvatarClickHandler(agentId, onOpenAgentSettings);
  const isExecuting =
    lifecycleState === "input-streaming" || lifecycleState === "input-available";

  const input = useMemo(() => parseJson(args), [args]);
  const output = useMemo(() => parseJson(result), [result]);

  const label = input?.label || "Worker";
  const taskDescription = input?.taskDescription || "";
  const enabledTools = input?.enabledTools || [];
  const resourceMode = input?.resourceMode || "inherit";
  const waitMode = input?.waitMode || "async";
  const model = input?.model;
  const maxIterations = input?.maxIterations;

  const workerSessionId = output?.workerSessionId;
  const workerAgentId = output?.agentId;
  const spawnStatus = output?.status;
  const displayStatus = liveWorkerStatus || (spawnStatus === "spawned" ? "IN_PROGRESS" : null);

  const workerHref =
    workerAgentId && workerSessionId
      ? buildAgentChatUrl(workerAgentId, workerSessionId)
      : workerAgentId
        ? `/agents/${workerAgentId}`
        : null;

  const truncatedTask =
    taskDescription.length > 140
      ? taskDescription.slice(0, 140) + "..."
      : taskDescription;

  const iterLabel = maxIterations ? `0/${maxIterations} iters` : null;
  const metaParts = [model, iterLabel, waitMode].filter(Boolean);

  return (
    <Box sx={{ ...messageRowSx, py: 0.4, pt: grouped ? 0.4 : 1.5 }}>
      <Box
        sx={{
          pl: grouped ? TEXT_INDENT : 0,
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
        }}
      >
        {!grouped && (
          <AgentAvatar agentId={agentId} name={agentName} size={AVATAR_SIZE} onClick={avatarClick} />
        )}
        <Box
          sx={{
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            overflow: "hidden",
            maxWidth: "75%",
            minWidth: "240px",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: "action.hover",
              ...(!compact && { cursor: "pointer" }),
            }}
            onClick={compact ? undefined : () => setExpanded(!expanded)}
          >
            <AccountTreeOutlinedIcon
              sx={{ fontSize: 15, color: "text.secondary" }}
            />
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 600,
                color: "text.primary",
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {label}
            </Typography>
            {isExecuting && !output && (
              <CircularProgress size={12} thickness={5} sx={{ ml: 0.5 }} />
            )}
            {displayStatus && <StatusBadge status={displayStatus} />}
            <CommonStatusPill
              variant={resourceMode === "dedicated" ? "working" : "neutral"}
              size="sm"
            >
              {resourceMode}
            </CommonStatusPill>
            {waitMode === "block" && (
              <PauseCircleOutlineIcon
                sx={{ fontSize: 16, color: "warning.main" }}
              />
            )}
            {!compact && (
              <ExpandMoreIcon
                sx={{
                  fontSize: 18,
                  color: "text.disabled",
                  transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 200ms",
                }}
              />
            )}
          </Box>

          {/* Collapsible body */}
          {!compact && (
            <Collapse in={expanded}>
              <Box sx={{ px: 1.5, py: 1, borderTop: "1px solid", borderColor: "divider" }}>
                {truncatedTask && (
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "text.secondary",
                      mb: 0.75,
                      lineHeight: 1.5,
                    }}
                  >
                    {truncatedTask}
                  </Typography>
                )}
                {enabledTools.length > 0 && (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.75 }}>
                    {enabledTools.map((t) => (
                      <CommonStatusPill
                        key={t}
                        variant="neutral"
                        size="sm"
                        style={{
                          fontFamily: "monospace",
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      >
                        {t}
                      </CommonStatusPill>
                    ))}
                  </Box>
                )}
                {metaParts.length > 0 && (
                  <Typography
                    sx={{
                      fontSize: "11px",
                      color: "text.disabled",
                      fontFamily: "monospace",
                    }}
                  >
                    {metaParts.join(" · ")}
                  </Typography>
                )}
              </Box>
            </Collapse>
          )}

          {/* Footer link */}
          {workerHref && (
            <Box
              sx={{
                px: 1.5,
                py: 0.6,
                borderTop: "1px solid",
                borderColor: "divider",
                display: "flex",
                alignItems: "center",
                gap: 0.5,
              }}
            >
              <a
                href={workerHref}
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: "none" }}
                onClick={(e) => e.stopPropagation()}
              >
                <Typography
                  sx={{
                    fontSize: "12px",
                    fontWeight: 500,
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    "&:hover": { textDecoration: "underline" },
                  }}
                >
                  Open worker conversation
                  <OpenInNewIcon sx={{ fontSize: 13 }} />
                </Typography>
              </a>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
